import React, { useCallback, useState } from "react";
import { BeakerIcon } from "@heroicons/react/24/outline";
import { PlatformSidebar } from "../../../components/layout";
import { PageHeader } from "../../../ui/PageHeader";
import { EmptyState } from "../../../ui/EmptyState";
import { Skeleton } from "../../../ui/Skeleton";
import type { Evaluator } from "../types";
import { EvaluatorsList } from "./EvaluatorsList";
import { EvaluatorDetail } from "./EvaluatorDetail";
import { CreateEvaluatorForm } from "./CreateEvaluatorForm";
import { EvaluatorsEmptyState } from "./EvaluatorsEmptyState";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

interface EvaluatorsViewProps {
  groupId?: string;
  evaluators: Evaluator[];
  isLoading: boolean;
  isSubmitting: boolean;
  isCreating: boolean;
  selectedEvaluator: Evaluator | null;
  onSelectEvaluator: (groupId: string) => void;
  onStartCreate: () => void;
  onCancelCreate: () => void;
  onCreate: (criteria: string, name?: string) => void;
  onUpdate: (id: string, criteria: string) => void;
  onDelete: (id: string) => void;
}

/** Where the user wanted to go before we stopped them over an unsaved draft. */
type PendingNav = { kind: "select"; groupId: string } | { kind: "create"; seed: string };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full h-full overflow-y-auto flex">
      <PlatformSidebar />
      <div className="flex-1 ml-48 mt-12 flex flex-col overflow-hidden">
        <div className="px-6">
          <PageHeader
            title="Evaluators"
            description="Define and manage the criteria that power your evaluations."
          />
        </div>
        <div className="flex flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

export function EvaluatorsView({
  groupId,
  evaluators,
  isLoading,
  isSubmitting,
  isCreating,
  selectedEvaluator,
  onSelectEvaluator,
  onStartCreate,
  onCancelCreate,
  onCreate,
  onUpdate,
  onDelete,
}: EvaluatorsViewProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [pendingNav, setPendingNav] = useState<PendingNav | null>(null);
  const [createSeed, setCreateSeed] = useState("");

  // Stable so the detail pane's effect doesn't re-fire on every render.
  const handleDirtyChange = useCallback((dirty: boolean) => setIsDirty(dirty), []);

  const go = useCallback(
    (nav: PendingNav) => {
      setIsDirty(false);
      if (nav.kind === "select") {
        onSelectEvaluator(nav.groupId);
      } else {
        setCreateSeed(nav.seed);
        onStartCreate();
      }
    },
    [onSelectEvaluator, onStartCreate]
  );

  /** Route every navigation through the unsaved-changes guard. */
  const requestNav = useCallback(
    (nav: PendingNav) => {
      if (isDirty) {
        setPendingNav(nav);
        return;
      }
      go(nav);
    },
    [isDirty, go]
  );

  if (isLoading) {
    // Mirror the loaded shell so the header never flickers and the panes don't pop in.
    return (
      <Shell>
        <div className="w-72 shrink-0 border-r border-border p-4 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={56} className="rounded-lg" />
          ))}
        </div>
        <div className="flex-1 p-6 max-w-3xl">
          <Skeleton variant="text" height={22} width={220} className="mb-4" />
          <Skeleton height={120} className="rounded-lg mb-4" />
          <Skeleton height={180} className="rounded-lg" />
        </div>
      </Shell>
    );
  }

  // With nothing to list, the 288px column and an empty detail pane are just furniture —
  // give the whole width to something that teaches instead.
  if (evaluators.length === 0 && !isCreating) {
    return (
      <Shell>
        <EvaluatorsEmptyState
          onStartFrom={(criteria) => go({ kind: "create", seed: criteria })}
          onStartBlank={() => go({ kind: "create", seed: "" })}
        />
      </Shell>
    );
  }

  const renderRightPanel = () => {
    if (isCreating) {
      return (
        <CreateEvaluatorForm
          isSubmitting={isSubmitting}
          initialCriteria={createSeed}
          onSave={onCreate}
          onCancel={() => {
            setIsDirty(false);
            onCancelCreate();
          }}
          onDirtyChange={handleDirtyChange}
        />
      );
    }
    if (selectedEvaluator) {
      return (
        <EvaluatorDetail
          evaluator={selectedEvaluator}
          isSubmitting={isSubmitting}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onDirtyChange={handleDirtyChange}
        />
      );
    }
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState
          icon={BeakerIcon}
          message="No evaluator selected"
          description="Select an evaluator from the list, or create a new one to define how evaluation works."
          actionLabel="Create evaluator"
          onAction={() => requestNav({ kind: "create", seed: "" })}
        />
      </div>
    );
  };

  return (
    <Shell>
      <EvaluatorsList
        evaluators={evaluators}
        selectedGroupId={groupId}
        onSelect={(nextGroupId) => requestNav({ kind: "select", groupId: nextGroupId })}
        onStartCreate={() => requestNav({ kind: "create", seed: "" })}
      />
      <div className="flex-1 overflow-hidden">{renderRightPanel()}</div>

      {pendingNav && (
        <UnsavedChangesDialog
          onCancel={() => setPendingNav(null)}
          onDiscard={() => {
            const nav = pendingNav;
            setPendingNav(null);
            go(nav);
          }}
        />
      )}
    </Shell>
  );
}
